import React, { useEffect, useState, useCallback } from 'react';
import cityLife from '../assets/animation/citylife.gif';
import dateSpot from '../assets/animation/datespot.gif';
import frostyMountains from '../assets/animation/frostymountains.gif';
import lateNightWalk from '../assets/animation/latenightwalk.gif';
import natureHearh from '../assets/animation/naturehearh.gif';
import parkWalk from '../assets/animation/parkwalk.gif';
import vacationHouse from '../assets/animation/vacationhouse.gif';
import RecentTodos from '../components/dashboard/recentTodos';
import VisitPanel from "../components/dashboard/VisitPanel";
import AnniversaryTimeline from "../components/dashboard/AnniversaryTimeline";
import '../styles/Home.css';

import { getAuth } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import axios from 'axios';
import { MOOD_OPTIONS, getMoodColor, getWelcomeGreeting } from '../constants';


const Home = () => {
    const [greeting, setGreeting] = useState('');
    const [partnerMood, setPartnerMood] = useState(null);
    const [partnerName, setPartnerName] = useState('');
    const [showMoodModal, setShowMoodModal] = useState(false);
    const [dashboardWallpaper, setDashboardWallpaper] = useState('');
    // Load dashboardWallpaper from localStorage on mount
    useEffect(() => {
        const wallpaper = localStorage.getItem('dashboardWallpaper') || '';
        setDashboardWallpaper(wallpaper);
        // Listen for changes from other tabs/windows
        const handler = (e) => {
            if (e.type === 'storage' && e.key === 'dashboardWallpaper') {
                setDashboardWallpaper(e.newValue || '');
            }
            if (e.type === 'frazza:wallpaperUpdated') {
                setDashboardWallpaper(e.detail?.name || '');
            }
        };
        window.addEventListener('storage', handler);
        window.addEventListener('frazza:wallpaperUpdated', handler);
        return () => {
            window.removeEventListener('storage', handler);
            window.removeEventListener('frazza:wallpaperUpdated', handler);
        };
    }, []);

    const fetchUserAndPartnerData = useCallback(async (uid) => {
        try {
            const userRef = doc(db, "UserInformation", uid);
            const docSnap = await getDoc(userRef);

            if (!docSnap.exists()) return;

            const data = docSnap.data();
            const fullName = data?.name || "Lovebird";
            const firstName = fullName.split(' ')[0];

            setGreeting(getWelcomeGreeting(firstName));

            // Fetch partner data if in a relationship
            if (data.inRelation && data.inRelationWith) {
                await fetchPartnerData(data.inRelationWith);
            }
        } catch (error) {
            console.error('Error fetching user data:', error);
        }
    }, []);

    const fetchPartnerData = async (partnerId) => {
        try {
            // Get partner's name
            const partnerRef = doc(db, "UserInformation", partnerId);
            const partnerSnap = await getDoc(partnerRef);
            
            if (partnerSnap.exists()) {
                const partnerData = partnerSnap.data();
                const partnerFullName = partnerData?.name || "Partner";
                setPartnerName(partnerFullName.split(' ')[0]);
            }

            // Fetch partner's mood from cycle tracking
            const response = await axios.get(`https://api.frazza.online/cycle/get?userId=${partnerId}`);
            if (response.data.success && response.data.data.dailyEntries) {
                const today = new Date().toISOString().split('T')[0];
                const todayEntry = response.data.data.dailyEntries.find(
                    entry => entry.date === today
                );
                if (todayEntry?.mood) {
                    setPartnerMood(todayEntry.mood);
                }
            }
        } catch (error) {
            // Silently fail - partner mood is optional
        }
    };

    useEffect(() => {
        const auth = getAuth();
        const uid = auth.currentUser?.uid;

        if (uid) {
            fetchUserAndPartnerData(uid);
        }
    }, [fetchUserAndPartnerData]);

    // Map dashboardWallpaper to imported image
    const wallpaperMap = {
        'city life.gif': cityLife,
        'date spot.gif': dateSpot,
        'frosty mountains.gif': frostyMountains,
        'late night walk.gif': lateNightWalk,
        'nature hearh.gif': natureHearh,
        'park walk.gif': parkWalk,
        'vacation house.gif': vacationHouse,
    };
    const wallpaperUrl = dashboardWallpaper ? wallpaperMap[dashboardWallpaper] : '';

    // Debug: log wallpaper info
    console.log('dashboardWallpaper:', dashboardWallpaper, 'wallpaperUrl:', wallpaperUrl);

    return (
        <div
            className={`home-container${dashboardWallpaper ? ' home-has-wallpaper' : ''}`}
            style={wallpaperUrl ? {
                backgroundImage: `url(${wallpaperUrl})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                minHeight: '100vh',
            } : {}}
        >
            <div className="greeting-container">
                {greeting && <h1 className="greeting-left">{greeting}</h1>}
                {partnerMood && (
                    <div 
                        className="partner-mood-circle" 
                        style={{ backgroundColor: getMoodColor(partnerMood) }}
                        onClick={() => setShowMoodModal(true)}
                        title={`${partnerName} is feeling ${partnerMood.toLowerCase()} today`}
                    />
                )}
            </div>
            <div className="dashboard-grid">
                <div className="dashboard-item div1">
                    <RecentTodos />
                </div>
                <div className="dashboard-item div2">
                    <VisitPanel />
                </div>
                <div className="dashboard-item div3">
                    <AnniversaryTimeline />
                </div>
            </div>

            {/* Mood Legend Modal */}
            {showMoodModal && (
                <div className="mood-modal-overlay" onClick={() => setShowMoodModal(false)}>
                    <div className="mood-modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="mood-modal-header">
                            <h3>Mood Guide</h3>
                            <button className="mood-modal-close" onClick={() => setShowMoodModal(false)}>×</button>
                        </div>
                        <div className="mood-modal-body">
                            <div className="mood-grid">
                                {MOOD_OPTIONS.map((mood) => (
                                    <div 
                                        key={mood.value} 
                                        className="mood-card"
                                        style={{ 
                                            backgroundColor: `${mood.color}20`,
                                            borderColor: mood.color
                                        }}
                                    >
                                        <div 
                                            className="mood-card-indicator" 
                                            style={{ backgroundColor: mood.color }}
                                        />
                                        <div className="mood-card-content">
                                            <h4 className="mood-card-title">{mood.value}</h4>
                                            <p className="mood-card-description">{mood.description}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                        {partnerMood && (
                            <div className="mood-modal-footer">
                                {partnerName} is feeling <strong>{partnerMood.toLowerCase()}</strong> today
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default Home;
